import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import DataTable from 'react-data-table-component';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { businessSummaryColumns, businessSummaryCustomStyles } from './BusinessSummaryColumns';
import {
  disasterColumns,
  disasterCustomStyles,
  transformToDisasterRows,
} from '../DisasterRegionColumns';
import { BusinessTableHeader } from './BusinessTableHeader';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import Button from 'src/ui/wf/common/button/Button';
import DropdownSelect from 'src/ui/wf/common/select/DropdownSelect';
import { WF_Screen } from 'src/ui/wf/router/WfScreens';
import { wfNavigate } from 'src/ui/wf/router/WfScreenRegistration';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { WfScreensMap } from 'src/ui/wf/router/WfScreensMap';
import { handleUpdateTimestamp } from 'src/ui/wf/common/util/dateUtils';
import { useWfTyphoonStore } from 'src/api/wf/stores/WfTyphoonStore';
import { WfSalesOfficeSummaryResponse } from 'src/api/wf/models/WfSummary';
import { FormRow } from 'src/ui/wf/common/form/FormRow';

export const BusinessSummary = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { currentTyphoon } = useWfTyphoonStore();
  const { salesOfficeOptions, filterParameters, patchFilterParameters } = useWfFilterStore();
  const branchCode = filterParameters.branch;

  const { fetchSummaryBySalesOffice, fetchSalesOfficeOptions, fetchDisasterAreaSalesOffices } =
    useWfWorkplaceService();

  const [tableData, setTableData] = useState<WfSalesOfficeSummaryResponse[]>([]);
  const [highlightedOffices, setHighlightedOffices] = useState<string[]>([]);

  const [lastClickedTime, setLastClickedTime] = useState<string | null>(() =>
    localStorage.getItem('last_business_click_time')
  );

  const zigzagData = useMemo(
    () => transformToDisasterRows(salesOfficeOptions),
    [salesOfficeOptions]
  );

  const handleFetchData = useCallback(async () => {
    if (currentTyphoon?.id && branchCode) {
      try {
        const finalData = await fetchSummaryBySalesOffice(
          currentTyphoon.id,
          branchCode,
          filterParameters.salesOffice || ''
        );
        setTableData(finalData || []);
      } catch (error) {
        console.error('Error fetching summary:', error);
        setTableData([]);
      }
    }
  }, [fetchSummaryBySalesOffice, currentTyphoon?.id, branchCode, filterParameters.salesOffice]);

  useEffect(() => {
    handleFetchData();
  }, [currentTyphoon?.id, branchCode, handleFetchData]);

  useEffect(() => {
    if (branchCode) {
      fetchSalesOfficeOptions(branchCode);
    }
  }, [branchCode, fetchSalesOfficeOptions]);

  useEffect(() => {
    const fetchHighlighting = async () => {
      if (currentTyphoon?.id && branchCode) {
        try {
          const codes = await fetchDisasterAreaSalesOffices(currentTyphoon.id, branchCode);
          setHighlightedOffices(codes || []);
        } catch (e) {
          console.error('Highlighting fetch failed', e);
          setHighlightedOffices([]);
        }
      }
    };
    fetchHighlighting();
  }, [currentTyphoon?.id, branchCode, fetchDisasterAreaSalesOffices]);

  const salesOfficeDropdownOptions = salesOfficeOptions.map((office) => ({
    title: `${office.code} ${office.name}`,
    value: office.code,
  }));

  const selectedSalesOfficeOption =
    salesOfficeDropdownOptions.find((option) => option.value === filterParameters.salesOffice) ||
    salesOfficeDropdownOptions[0];

  const handleSalesOfficeChanged = (event: ChangeEvent<HTMLSelectElement>) => {
    patchFilterParameters({ salesOffice: event.target.value });
  };

  const handleButtonClick = () => {
    handleUpdateTimestamp('last_business_click_time', setLastClickedTime);
    handleFetchData();
  };

  useEffect(() => {
    handleUpdateTimestamp('last_business_click_time', setLastClickedTime);
  }, []);

  const backNavigation = () => {
    wfNavigate(navigate, WfScreensMap[WF_Screen.BranchSummary].path);
  };

  return (
    <div className="mx-auto flex max-w-fit flex-col gap-2 py-2">
      <div className="flex flex-col gap-2">
        <div className="flex flex-col items-center gap-4 rounded-sm border-2 border-sky-600 p-3 shadow-sm sm:flex-row">
          <div className="w-full flex-1">
            <FormRow title={t('wf.common.salesOffice')} required>
              <DropdownSelect
                selectedValue={selectedSalesOfficeOption}
                optionValues={salesOfficeDropdownOptions}
                onChange={handleSalesOfficeChanged}
              />
            </FormRow>
          </div>
          <Button title={t('wf.common.update')} onClick={handleButtonClick} />
          {lastClickedTime && (
            <span className="self-center text-sm text-gray-600">{lastClickedTime}</span>
          )}
        </div>
        <div className="w-full rounded-sm border-2 border-sky-600 bg-gray-50 p-3 text-center text-sm">
          {t('wf.common.displayedContent')}
        </div>
      </div>

      <div className="flex h-fit flex-col overflow-hidden rounded-sm border border-gray-300 shadow-sm">
        <DataTable
          noDataComponent={<div className="p-4">{t('wf.common.noResults')}</div>}
          noTableHead
          columns={disasterColumns(highlightedOffices)}
          data={zigzagData}
          customStyles={disasterCustomStyles}
          dense
        />
      </div>
      <div className="flex w-full flex-col">
        <div className="mb-2 text-sm font-semibold tracking-wide">
          {t('wf.summary.salesOffice.title')}
        </div>

        <div className="w-full overflow-x-auto rounded-sm border border-gray-300 shadow-sm">
          <div className="inline-block min-w-max align-middle">
            <BusinessTableHeader t={t} />
            <DataTable
              noDataComponent={<div className="p-4">{t('wf.common.noResults')}</div>}
              noTableHead
              columns={businessSummaryColumns()}
              data={tableData}
              customStyles={businessSummaryCustomStyles}
              dense
              pagination
              paginationPerPage={10}
            />
          </div>
        </div>
      </div>

      <Button title={t('wf.common.back')} onClick={backNavigation} className="self-start" />
    </div>
  );
};
